import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Sparkles } from "lucide-react";

// Guided spotlight tour. `steps` = [{ target: "[data-testid=...]", title, body }].
// Steps whose target is missing from the DOM are skipped automatically.
export default function Tour({ steps, onClose }) {
  const [items] = useState(() => steps.filter((s) => document.querySelector(s.target)));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = items[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    try { el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }); } catch {}
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    const t = setTimeout(measure, 320);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [measure]);

  if (!step) return null;

  const last = i >= items.length - 1;
  const next = () => (last ? onClose() : setI((v) => v + 1));
  const back = () => setI((v) => Math.max(0, v - 1));

  // Tooltip placement: below the target, clamped into the viewport.
  const CARD_W = 320;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  let tipTop = rect ? rect.top + rect.height + 14 : vh / 2;
  let tipLeft = rect ? Math.min(Math.max(rect.left, 12), vw - CARD_W - 12) : (vw - CARD_W) / 2;
  const below = rect ? rect.top + rect.height + 200 < vh : true;
  if (rect && !below) tipTop = Math.max(12, rect.top - 200);

  return (
    <div data-testid="tour-overlay" className="fixed inset-0 z-[190]">
      {/* Spotlight hole */}
      {rect && (
        <motion.div
          initial={false}
          animate={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="fixed rounded-xl pointer-events-none"
          style={{ boxShadow: "0 0 0 9999px rgba(15,23,42,0.60)" }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          data-testid="tour-tooltip"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25 }}
          className="fixed w-[320px] rounded-2xl bg-white shadow-2xl border border-amber-100 p-5"
          style={{ top: tipTop, left: tipLeft }}
        >
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-[#EBF3FB] flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#1E5AA8]" /></span>
            <h4 className="font-semibold text-slate-900 text-sm">{step.title}</h4>
          </div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.body}</p>

          <div className="mt-4 flex items-center justify-between">
            <span data-testid="tour-step-indicator" className="text-xs text-slate-400" data-notranslate>{i + 1} / {items.length}</span>
            <div className="flex items-center gap-2">
              {i > 0 && (
                <button data-testid="tour-back" onClick={back}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 px-2 py-1.5 rounded-lg">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              <button data-testid="tour-next" onClick={next}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#1E5AA8] hover:bg-[#123C73] px-3.5 py-1.5 rounded-lg transition-colors">
                {last ? "Finish" : "Next"} {!last && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button data-testid="tour-skip" onClick={onClose}
            className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700"
            title="Skip tour">
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function doctorTourSteps() {
  return [
    { target: "[data-testid=nav-tab-home]", title: "Your Home cockpit", body: "See today's patients, follow-up reminders and recent consultations at a glance." },
    { target: "[data-testid=nav-tab-consultation]", title: "Live Consult & AI Rx", body: "Record the consultation and let the AI draft the diagnosis, tests and prescription." },
    { target: "[data-testid=nav-tab-askme]", title: "Ask Me", body: "Ask the clinical assistant any medical question and get a concise, evidence-based answer." },
    { target: "[data-testid=nav-tab-book]", title: "Book Appointment", body: "Schedule new or existing patients in seconds and attach previous reports." },
    { target: "[data-testid=nav-tab-appointments]", title: "Booked Appointments", body: "View and manage every appointment, neatly grouped by day." },
    { target: "[data-testid=nav-tab-prescriptions]", title: "Prescription Pad", body: "Create and print prescriptions and medical certificates as PDF or Word." },
    { target: "[data-testid=nav-tab-followups]", title: "Patients & Follow-ups", body: "Your full patient registry with follow-up tracking and reminders." },
    { target: "[data-testid=nav-tab-inventory]", title: "Medicines & Labs", body: "Search the medicine and lab-test directory with quick autocomplete." },
    { target: "[data-testid=nav-tab-profile]", title: "Your Profile", body: "Set clinic details, signature, stamp and the contacts used to share prescriptions." },
    { target: "[data-testid=language-switcher]", title: "Any language", body: "Switch the entire interface to your preferred language, any time." },
  ];
}

export function assistantTourSteps() {
  return [
    { target: "[data-testid=nav-tab-book]", title: "Book Appointment", body: "Schedule new or existing patients for your doctor in seconds." },
    { target: "[data-testid=nav-tab-appointments]", title: "Booked Appointments", body: "View and manage the day's appointments, grouped by date." },
    { target: "[data-testid=nav-tab-profile]", title: "Your Profile", body: "Manage your account details and linked doctor here." },
    { target: "[data-testid=language-switcher]", title: "Any language", body: "Switch the entire interface to your preferred language, any time." },
  ];
}
